import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import ClientLayout from '../../../components/layout/ClientLayout';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { useAuth } from '../../../hooks/useAuth';
import SmsService from '../../../services/SmsService';

interface UserProfile {
  email: string;
  name: string;
  organization: string;
  phoneNumber: string;
  userLevel: number;
}

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    email: '',
    name: '',
    organization: '',
    phoneNumber: '',
    userLevel: 9
  });
  const [editForm, setEditForm] = useState<Omit<UserProfile, 'email' | 'userLevel'>>({
    name: '',
    organization: '',
    phoneNumber: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const currentPasswordRef = useRef<HTMLInputElement>(null);
  const { loading: emailVerificationLoading } = useEmailVerification();

  // SMS 인증 관련 상태
  const [sendingSms, setSendingSms] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationSuccess, setVerificationSuccess] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [originalPhoneNumber, setOriginalPhoneNumber] = useState('');
  
  // 타이머 관리를 위한 ref
  const verificationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 현재 인증 상태를 ref로 추적 (타이머에서 최신 상태 확인용)
  const phoneVerifiedRef = useRef(phoneVerified);

  // phoneVerified 상태 변경 시 ref도 업데이트
  useEffect(() => {
    phoneVerifiedRef.current = phoneVerified;
  }, [phoneVerified]);

  // 전화번호 인증 생략 도메인 체크
  const isExemptDomain = (email: string): boolean => {
    const exemptDomains = ['bnfc.or.kr', 'korea.kr', 'busanpa.com'];
    const domain = email.split('@')[1];
    return exemptDomains.includes(domain);
  };

  // 전화번호가 변경되었는지 확인
  const isPhoneNumberChanged = (): boolean => {
    return editForm.phoneNumber !== originalPhoneNumber;
  };

  // 인증 필요 여부 확인
  const needsPhoneVerification = (): boolean => {
    return isPhoneNumberChanged() && !isExemptDomain(userProfile.email);
  };

  // 기존 타이머들 정리
  const clearTimers = () => {
    if (verificationTimerRef.current) {
      clearInterval(verificationTimerRef.current);
      verificationTimerRef.current = null;
    }
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
  };

  // 인증번호 입력 타이머 시작 (3분)
  const startTimer = () => {
    // 기존 타이머가 있다면 정리
    if (verificationTimerRef.current) {
      clearInterval(verificationTimerRef.current);
    }
    
    verificationTimerRef.current = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          clearInterval(verificationTimerRef.current!);
          verificationTimerRef.current = null;
          
          // 인증이 아직 완료되지 않은 경우에만 만료 메시지 표시
          if (!phoneVerifiedRef.current) {
            setShowVerificationInput(false);
            setVerificationError('인증 시간이 만료되었습니다.');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 재발송 방지 쿨다운 타이머 시작 (1분)
  const startCooldownTimer = () => {
    // 기존 타이머가 있다면 정리
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
    
    cooldownTimerRef.current = setInterval(() => {
      setCooldownTime(prev => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current!);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 시간 포맷 (mm:ss)
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // SMS 인증번호 발송
  const handleSendVerification = async () => {
    if (!editForm.phoneNumber) {
      setVerificationError('전화번호를 입력해주세요.');
      return;
    }

    // 전화번호 유효성 검사
    const normalizedPhone = editForm.phoneNumber.replace(/-/g, '');
    if (!/^01[0-9]{8,9}$/.test(normalizedPhone)) {
      setVerificationError('올바른 전화번호 형식이 아닙니다.');
      return;
    }

    setSendingSms(true);
    setVerificationError(null);
    setVerificationSuccess(null);

    try {
      const { requestId } = await SmsService.sendVerificationCodeForSignup(normalizedPhone);
      
      setShowVerificationInput(true);
      setVerificationSuccess('인증번호가 발송되었습니다. 3분 이내에 입력해주세요.');
      
      // 인증번호 입력 타이머 시작 (3분)
      setRemainingTime(180); // 3분 = 180초
      startTimer();
      
      // 재발송 방지 쿨다운 시작 (1분)
      setCooldownTime(60); // 1분 = 60초
      startCooldownTimer();
      
    } catch (error) {
      console.error('SMS 발송 실패:', error);
      setVerificationError(error instanceof Error ? error.message : 'SMS 발송에 실패했습니다.');
    } finally {
      setSendingSms(false);
    }
  };

  // 인증번호 검증
  const handleVerifyCode = async () => {
    if (!editForm.phoneNumber || !verificationCode) {
      setVerificationError('인증번호를 입력해주세요.');
      return;
    }

    try {
      const normalizedPhone = editForm.phoneNumber.replace(/-/g, '');
      const isValid = await SmsService.verifyCodeForSignup(normalizedPhone, verificationCode);
      
      if (isValid) {
        clearTimers(); // 모든 타이머 정리
        setVerificationSuccess('전화번호 인증이 완료되었습니다.');
        setPhoneVerified(true);
        setShowVerificationInput(false);
        setVerificationCode('');
        setRemainingTime(0);
        setCooldownTime(0); // 인증 성공 시 쿨다운 초기화
        setVerificationError(null);
      } else {
        setVerificationError('인증번호가 올바르지 않거나 만료되었습니다.');
      }
    } catch (error) {
      console.error('인증번호 확인 실패:', error);
      setVerificationError(error instanceof Error ? error.message : '인증번호 확인에 실패했습니다.');
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      // authLoading이 완료될 때까지 대기
      if (authLoading) {
        return;
      }

      // 인증 로딩이 완료된 후 user가 없으면 로그인 페이지로 이동
      if (!user) {
        navigate('/signin');
        return;
      }

      try {
        const userDocRef = doc(db, 'user_profiles', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data() as UserProfile;
          setUserProfile(data);
          setEditForm({
            name: data.name,
            organization: data.organization,
            phoneNumber: data.phoneNumber
          });
          setOriginalPhoneNumber(data.phoneNumber); // 원본 전화번호 저장
        }
      } catch (err) {
        console.error('프로필 로딩 오류:', err);
        setError('프로필 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (showPasswordForm && currentPasswordRef.current) {
      currentPasswordRef.current.focus();
      currentPasswordRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showPasswordForm]);

  // 전화번호 변경 시 인증 상태 초기화
  useEffect(() => {
    if (!isEditing) return;
    
    if (isPhoneNumberChanged()) {
      clearTimers(); // 기존 타이머들 정리
      setPhoneVerified(false);
      setShowVerificationInput(false);
      setVerificationCode('');
      setVerificationError(null);
      setVerificationSuccess(null);
      setRemainingTime(0);
      setCooldownTime(0);
      
      // 특정 도메인의 경우 인증 생략
      if (isExemptDomain(userProfile.email)) {
        setPhoneVerified(true);
        setVerificationSuccess('해당 도메인은 전화번호 인증이 생략됩니다.');
      }
    } else {
      // 전화번호가 원래대로 돌아온 경우 인증 상태 초기화
      clearTimers();
      setPhoneVerified(false);
      setShowVerificationInput(false);
      setVerificationCode('');
      setVerificationError(null);
      setVerificationSuccess(null);
      setRemainingTime(0);
      setCooldownTime(0);
    }
  }, [editForm.phoneNumber, isEditing, userProfile.email, originalPhoneNumber]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      name: userProfile.name,
      organization: userProfile.organization,
      phoneNumber: userProfile.phoneNumber
    });
    // SMS 인증 상태 초기화
    clearTimers();
    setPhoneVerified(false);
    setShowVerificationInput(false);
    setVerificationCode('');
    setVerificationError(null);
    setVerificationSuccess(null);
    setRemainingTime(0);
    setCooldownTime(0);
  };

  const handleSaveProfile = async () => {
    if (!user) {
      navigate('/signin');
      return;
    }

    try {
      // 전화번호가 변경되었고 인증이 필요한 경우 인증 완료 여부 확인
      if (needsPhoneVerification() && !phoneVerified) {
        setError('전화번호 인증을 완료해주세요.');
        return;
      }

      setLoading(true);
      const userDocRef = doc(db, 'user_profiles', user.uid);
      
      const updateData: any = {
        name: editForm.name,
        organization: editForm.organization,
        phoneNumber: editForm.phoneNumber,
        updatedAt: new Date()
      };

      // 전화번호가 변경된 경우 인증 상태도 업데이트
      if (isPhoneNumberChanged()) {
        updateData.phoneVerified = phoneVerified;
      }
      
      await updateDoc(userDocRef, updateData);

      setUserProfile(prev => ({
        ...prev,
        ...editForm
      }));
      
      setOriginalPhoneNumber(editForm.phoneNumber); // 새로운 전화번호를 원본으로 설정
      setIsEditing(false);
      
      // SMS 인증 상태 초기화
      clearTimers();
      setPhoneVerified(false);
      setShowVerificationInput(false);
      setVerificationCode('');
      setVerificationError(null);
      setVerificationSuccess(null);
      setRemainingTime(0);
      setCooldownTime(0);
      
      alert('프로필이 성공적으로 업데이트되었습니다.');
    } catch (err) {
      console.error('프로필 업데이트 오류:', err);
      setError('프로필 업데이트 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleLevelUpRequest = () => {
    navigate('/level-up-request');
  };

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmNewPassword) {
      setPasswordError('모든 비밀번호 입력란을 채워주세요.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      setPasswordError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('새 비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    try {
      setPasswordLoading(true);
      if (!user || !user.email) {
        setPasswordError('로그인 정보가 유효하지 않습니다.');
        return;
      }
      // 현재 비밀번호로 재인증
      const credential = EmailAuthProvider.credential(user.email, passwordForm.currentPassword);
      await reauthenticateWithCredential(user, credential);
      // 비밀번호 변경
      await updatePassword(user, passwordForm.newPassword);
      setPasswordSuccess('비밀번호가 성공적으로 변경되었습니다.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    } catch (err: any) {
      if (err.code === 'auth/wrong-password') {
        setPasswordError('현재 비밀번호가 올바르지 않습니다.');
      } else if (err.code === 'auth/weak-password') {
        setPasswordError('새 비밀번호가 너무 약합니다.');
      } else {
        setPasswordError('비밀번호 변경 중 오류가 발생했습니다.');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const getRoleLabel = (userLevel: number): string => {
    switch (userLevel) {
      case 1:
        return '최고 관리자';
      case 2:
        return '일반 관리자';
      case 3:
        return '공사 관계자';
      case 4:
        return '센터 관계자';
      case 5:
        return '입주업체';
      case 6:
        return '입주협력업체';
      case 7:
        return '공공기관';
      case 8:
        return '항만근로자';
      case 9:
        return '일반 사용자';
      default:
        return '일반 사용자';
    }
  };

  if (authLoading || loading || emailVerificationLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg">로딩중...</div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-16 py-6 md:py-12">
          <div className="w-[calc(100%-32px)] md:w-[760px] mx-auto relative">
            {/* 헤더 섹션 */}
            <div className="mt-4 md:mt-8">
              <div className="text-slate-800 text-2xl md:text-4xl font-extrabold font-['Pretendard'] leading-9">
                회원정보
              </div>
              <div className="mt-3 md:mt-4 text-slate-700 text-lg md:text-xl font-normal font-['Pretendard'] leading-9">
                내 회원정보를 확인하고 수정할 수 있습니다
              </div>
            </div>

            {/* 구분선 */}
            <div className="w-full h-px mt-6 md:mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

            {error && (
              <div className="mt-6 md:mt-8 bg-red-50 border-l-4 border-red-400 p-3 md:p-4 rounded">
                <p className="text-red-700 text-sm md:text-base">{error}</p>
              </div>
            )}

            <div className="mt-6 md:mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-4 md:p-8">
                <h2 className="text-[#1e2c49] text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-3 md:mb-4">
                  기본 정보
                </h2>
                <div className="w-full md:w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 md:mb-8"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div>
                  <p className="text-[#6b7684] text-sm md:text-lg font-medium mb-2">이메일</p>
                  <p className="text-[#1e2c49] text-base md:text-[19px] font-semibold break-all">{userProfile.email}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-sm md:text-lg font-medium mb-2">이름</p>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full h-10 md:h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-3 md:px-4 text-[#1e2c49] text-base md:text-[19px] font-semibold"
                    />
                  ) : (
                    <p className="text-[#1e2c49] text-base md:text-[19px] font-semibold">{userProfile.name}</p>
                  )}
                </div>
                <div>
                  <p className="text-[#6b7684] text-sm md:text-lg font-medium mb-2">소속</p>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.organization}
                      onChange={(e) => setEditForm(prev => ({ ...prev, organization: e.target.value }))}
                      className="w-full h-10 md:h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-3 md:px-4 text-[#1e2c49] text-base md:text-[19px] font-semibold"
                    />
                  ) : (
                    <p className="text-[#1e2c49] text-base md:text-[19px] font-semibold">{userProfile.organization || '정보 없음'}</p>
                  )}
                </div>
                <div>
                  <p className="text-[#6b7684] text-sm md:text-lg font-medium mb-2">연락처</p>
                  {isEditing ? (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="tel"
                        value={editForm.phoneNumber}
                        onChange={(e) => setEditForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                        className="flex-1 h-10 md:h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-3 md:px-4 text-[#1e2c49] text-base md:text-[19px] font-semibold"
                      />
                      {needsPhoneVerification() && !phoneVerified && !showVerificationInput && (
                        <button
                          type="button"
                          onClick={handleSendVerification}
                          disabled={sendingSms || !editForm.phoneNumber}
                          className="w-full sm:w-24 md:w-32 h-10 md:h-12 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-sm font-bold font-['Pretendard'] text-sm md:text-lg text-white"
                        >
                          {sendingSms ? '발송중...' : '인증'}
                        </button>
                      )}
                      {needsPhoneVerification() && !phoneVerified && showVerificationInput && (
                        <div className="flex items-center justify-center w-full sm:w-24 md:w-32 h-10 md:h-12 bg-gray-100 text-gray-600 rounded-sm font-bold font-['Pretendard'] text-sm md:text-lg">
                          발송완료
                        </div>
                      )}
                      {needsPhoneVerification() && phoneVerified && (
                        <div className="flex items-center justify-center w-full sm:w-24 md:w-32 h-10 md:h-12 bg-green-100 text-green-700 rounded-sm font-bold font-['Pretendard'] text-sm md:text-lg">
                          ✓ 인증완료
                        </div>
                      )}
                      {isPhoneNumberChanged() && isExemptDomain(userProfile.email) && (
                        <div className="flex items-center justify-center w-full sm:w-24 md:w-32 h-10 md:h-12 bg-gray-100 text-gray-600 rounded-sm font-bold font-['Pretendard'] text-sm md:text-lg">
                          인증생략
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-[#1e2c49] text-base md:text-[19px] font-semibold">{userProfile.phoneNumber || '정보 없음'}</p>
                  )}
                </div>

                {/* 인증번호 입력 필드 (전화번호 변경 시에만 표시) */}
                {isEditing && showVerificationInput && (
                  <div className="col-span-1 md:col-span-2">
                    <p className="text-[#6b7684] text-sm md:text-lg font-medium mb-2">인증번호</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="인증번호 6자리"
                        className="flex-1 h-10 md:h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-3 md:px-4 text-[#1e2c49] text-base md:text-[19px] font-semibold"
                        maxLength={6}
                      />
                      <button
                        type="button"
                        onClick={handleVerifyCode}
                        disabled={!verificationCode || verificationCode.length !== 6}
                        className="w-full sm:w-24 md:w-32 h-10 md:h-12 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 rounded-sm font-bold font-['Pretendard'] text-sm md:text-lg text-white"
                      >
                        확인
                      </button>
                      {needsPhoneVerification() && !phoneVerified && (
                        <button
                          type="button"
                          onClick={handleSendVerification}
                          disabled={sendingSms || !editForm.phoneNumber || cooldownTime > 0}
                          className="w-full sm:w-24 md:w-32 h-10 md:h-12 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-sm font-bold font-['Pretendard'] text-sm md:text-lg text-white"
                        >
                          {sendingSms ? '발송중...' : cooldownTime > 0 ? `재발송 (${cooldownTime}초)` : '재발송'}
                        </button>
                      )}
                    </div>
                    {remainingTime > 0 && (
                      <p className="mt-2 text-xs md:text-sm text-gray-500 font-['Pretendard']">
                        남은 시간: {formatTime(remainingTime)}
                      </p>
                    )}
                  </div>
                )}

                {/* SMS 인증 관련 메시지 */}
                {isEditing && verificationError && (
                  <div className="col-span-1 md:col-span-2">
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 md:px-4 py-2 md:py-3 rounded font-['Pretendard'] text-sm md:text-base">
                      {verificationError}
                    </div>
                  </div>
                )}

                {isEditing && verificationSuccess && (
                  <div className="col-span-1 md:col-span-2">
                    <div className="bg-green-50 border border-green-200 text-green-700 px-3 md:px-4 py-2 md:py-3 rounded font-['Pretendard'] text-sm md:text-base">
                      {verificationSuccess}
                    </div>
                  </div>
                )}

                <div className="col-span-1 md:col-span-2">
                  <p className="text-[#6b7684] text-sm md:text-lg font-medium mb-2">회원 등급</p>
                  <p className="text-[#1e2c49] text-base md:text-[19px] font-semibold">
                    {getRoleLabel(userProfile.userLevel)}
                  </p>
                </div>
              </div>
            </div>
            {/* 비밀번호 변경 섹션 (수정 모드에서만) */}
            {isEditing && (
              <div className="p-4 md:p-8 pt-0">
                {!showPasswordForm ? (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowPasswordForm(true)}
                      className="w-full sm:w-36 md:w-44 h-10 md:h-12 bg-[#155acb] hover:bg-[#155acb]/90 rounded-sm font-bold font-['Pretendard'] text-base md:text-lg text-white"
                    >
                      비밀번호 변경
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-[#1e2c49] text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-3 md:mb-4 mt-6 md:mt-8">
                      비밀번호 변경
                    </h2>
                    <div className="w-full md:w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 md:mb-8"></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                      <div>
                        <p className="text-[#6b7684] text-sm md:text-lg font-medium mb-2">현재 비밀번호</p>
                        <input
                          ref={currentPasswordRef}
                          type="password"
                          name="currentPassword"
                          value={passwordForm.currentPassword}
                          onChange={handlePasswordInputChange}
                          className="w-full h-10 md:h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-3 md:px-4 text-[#1e2c49] text-base md:text-[19px] font-semibold"
                          autoComplete="current-password"
                        />
                      </div>
                      <div className="hidden md:block"></div>
                      <div>
                        <p className="text-[#6b7684] text-sm md:text-lg font-medium mb-2">새 비밀번호</p>
                        <input
                          type="password"
                          name="newPassword"
                          value={passwordForm.newPassword}
                          onChange={handlePasswordInputChange}
                          className="w-full h-10 md:h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-3 md:px-4 text-[#1e2c49] text-base md:text-[19px] font-semibold"
                          autoComplete="new-password"
                        />
                      </div>
                      <div>
                        <p className="text-[#6b7684] text-sm md:text-lg font-medium mb-2">새 비밀번호 확인</p>
                        <input
                          type="password"
                          name="confirmNewPassword"
                          value={passwordForm.confirmNewPassword}
                          onChange={handlePasswordInputChange}
                          className="w-full h-10 md:h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-3 md:px-4 text-[#1e2c49] text-base md:text-[19px] font-semibold"
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                    {passwordError && (
                      <div className="mt-3 md:mt-4 text-red-600 text-sm md:text-base font-medium">{passwordError}</div>
                    )}
                    {passwordSuccess && (
                      <div className="mt-3 md:mt-4 text-green-600 text-sm md:text-base font-medium">{passwordSuccess}</div>
                    )}
                    <div className="flex flex-col sm:flex-row justify-end mt-6 md:mt-8 gap-3 md:gap-4">
                      <button
                        type="button"
                        onClick={() => setShowPasswordForm(false)}
                        className="w-full sm:w-36 md:w-44 h-10 md:h-12 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-sm font-bold font-['Pretendard'] text-base md:text-lg"
                        disabled={passwordLoading}
                      >
                        취소
                      </button>
                      <button
                        type="button"
                        onClick={handleChangePassword}
                        disabled={passwordLoading}
                        className={`w-full sm:w-36 md:w-44 h-10 md:h-12 ${!passwordLoading ? 'bg-[#155acb] hover:bg-[#155acb]/90' : 'bg-gray-400'} rounded-sm font-bold font-['Pretendard'] text-base md:text-lg text-white`}
                      >
                        {passwordLoading ? '변경 중...' : '비밀번호 변경'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            </div>

            {/* 하단 버튼 */}
            <div className="fixed bottom-0 left-0 w-full h-20 md:h-24 bg-white border-t border-gray-200 z-10">
              <div className="max-w-[1600px] mx-auto px-4 h-full flex items-center justify-end">
                {isEditing ? (
                  <div className="flex flex-col sm:flex-row gap-2 md:gap-4 w-full sm:w-auto">
                    {/* 등업신청 버튼 - 의도적으로 숨김 */}
                    {false && userProfile.userLevel === 9 && (
                      <button
                        onClick={handleLevelUpRequest}
                        className="w-full sm:w-32 md:w-44 h-12 md:h-16 rounded-sm border border-green-600 text-green-600 hover:bg-green-50 font-bold font-['Pretendard'] text-base md:text-lg order-3 sm:order-1"
                      >
                        등업신청
                      </button>
                    )}
                    <button
                      onClick={handleCancelEdit}
                      className="w-full sm:w-32 md:w-44 h-12 md:h-16 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-50 font-bold font-['Pretendard'] text-base md:text-lg order-2 sm:order-1"
                      disabled={loading}
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      className={`w-full sm:w-32 md:w-44 h-12 md:h-16 ${!loading && (!needsPhoneVerification() || phoneVerified) ? 'bg-[#155acb] hover:bg-[#155acb]/90' : 'bg-gray-400'} rounded-sm font-bold font-['Pretendard'] text-base md:text-lg text-white order-1 sm:order-2`}
                      disabled={loading || (needsPhoneVerification() && !phoneVerified)}
                    >
                      {loading ? '저장 중...' : '저장'}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleEditClick}
                    className="w-full sm:w-32 md:w-44 h-12 md:h-16 bg-[#155acb] hover:bg-[#155acb]/90 rounded-sm font-bold font-['Pretendard'] text-base md:text-lg text-white"
                  >
                    수정
                  </button>
                )}
              </div>
            </div>

            {/* 하단 여백 */}
            <div className="h-24 md:h-32"></div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default SettingsPage; 